import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { useLang } from "@/i18n";
import { CheckCircle2, Loader2, MapPin, Calendar, Car, CreditCard } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const { t } = useLang();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("loading");
  const [booking, setBooking] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          const conf = await axios.get(`${API}/bookings/confirmation/${sessionId}`);
          setBooking(conf.data);
          setStatus("paid");
          return;
        }
      } catch (e) {
        // keep polling
      }
      attempts.current += 1;
      if (attempts.current < 20) {
        setTimeout(poll, 2500);
      } else {
        setStatus("timeout");
      }
    };
    poll();
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#060709] text-[#f4f4f7] flex items-center justify-center px-6" data-testid="payment-success-page">
      <div className="gold-card rounded-2xl p-8 md:p-12 max-w-xl w-full">
        {status === "paid" && booking ? (
          <div data-testid="booking-confirmed">
            <div className="flex items-center gap-3 mb-6">
              <CheckCircle2 className="w-10 h-10 text-[#34d399]" />
              <div>
                <h1 className="font-disp text-2xl md:text-3xl text-white">{t("succ.confirmed")}</h1>
                <p className="font-mono text-xs text-[#c9a24d] uppercase tracking-widest">{t("succ.ref")} {booking.reference}</p>
              </div>
            </div>
            <div className="space-y-3 text-sm text-[#c6cedb]">
              <div className="flex items-start gap-3" data-testid="conf-route">
                <MapPin className="w-4 h-4 text-[#c9a24d] mt-0.5 shrink-0" />
                <span>{booking.pickup} → {booking.destination}</span>
              </div>
              <div className="flex items-center gap-3" data-testid="conf-datetime">
                <Calendar className="w-4 h-4 text-[#c9a24d] shrink-0" />
                <span>{booking.date} {t("succ.at")} {booking.time} · {booking.distance_km} km (~{booking.duration_min} min)</span>
              </div>
              <div className="flex items-center gap-3" data-testid="conf-vehicle">
                <Car className="w-4 h-4 text-[#c9a24d] shrink-0" />
                <span>{booking.vehicle_label}</span>
              </div>
              <div className="flex items-center gap-3" data-testid="conf-price">
                <CreditCard className="w-4 h-4 text-[#c9a24d] shrink-0" />
                <span>{t("succ.paidPrefix")} <strong className="text-[#c9a24d]">{booking.price_eur.toFixed(2)} €</strong></span>
              </div>
            </div>
            <p className="mt-6 text-xs text-[#8c98a9]">
              {t("succ.thanks", { name: booking.name })}
            </p>
            <Link to="/" data-testid="back-home-link"
              className="mt-6 inline-block font-geom px-6 py-3 text-xs font-bold uppercase tracking-[0.2em] bg-gradient-to-r from-[#c9a24d] via-[#d9b86c] to-[#a17c2e] text-[#060709] rounded-full">
              {t("succ.back")}
            </Link>
          </div>
        ) : status === "error" || status === "timeout" ? (
          <div className="text-center" data-testid="payment-status-error">
            <h1 className="font-disp text-2xl text-white mb-3">{t("succ.pendingTitle")}</h1>
            <p className="text-sm text-[#8c98a9] mb-6">
              {status === "error" ? t("succ.notFound") : t("succ.pendingText")}
            </p>
            <Link to="/" className="font-geom px-6 py-3 text-xs font-bold uppercase tracking-[0.2em] bg-[#12151e] text-[#c9a24d] rounded-full border border-[#c9a24d]/40" data-testid="back-home-link">
              {t("succ.back")}
            </Link>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-4 py-8" data-testid="payment-verifying">
            <Loader2 className="w-10 h-10 text-[#c9a24d] animate-spin" />
            <p className="font-mono text-xs uppercase tracking-widest text-[#8c98a9]">{t("succ.verifying")}</p>
          </div>
        )}
      </div>
    </div>
  );
}
